import { TextField, type TextFieldProps } from "./TextField";

/**
 * Para qué sirve este correo.
 *
 * - `account` — es el identificador con el que alguien entra. Va con
 *   `autocomplete="username"`, que es el token que navegadores y gestores
 *   de contraseñas buscan para emparejarlo con `current-password` o
 *   `new-password` y guardar la credencial completa.
 * - `contact` — es un correo como dato, no como llave: el del titular de
 *   una factura, el de un contacto de soporte. Ahí `autocomplete="email"`
 *   es lo correcto y no debe contaminar el llavero de credenciales.
 */
export type EmailPurpose = "account" | "contact";

const AUTOCOMPLETE_BY_PURPOSE: Record<EmailPurpose, string> = {
  account: "username",
  contact: "email",
};

export interface EmailFieldProps extends Omit<TextFieldProps, "name"> {
  /** Por defecto `"email"`, que es como se llama en todos los contratos. */
  name?: string;
  /** Por defecto `"contact"`: el uso genérico. Login y registro piden `"account"`. */
  purpose?: EmailPurpose;
}

/**
 * Un campo de correo que SABE que es un campo de correo.
 *
 * Existe por un defecto real: el login declaraba `autoComplete="username"`
 * y el registro `autoComplete="email"` para el MISMO dato. Dos pantallas,
 * dos respuestas, y quien usa un gestor de contraseñas paga la diferencia.
 * El teclado móvil también: sin `type`/`inputMode` de correo aparece el
 * teclado de texto, sin la arroba a mano.
 *
 * La unificación correcta no era elegir uno de los dos valores para
 * siempre, sino nombrar la diferencia: ver `EmailPurpose`.
 *
 * Todo sigue siendo sobrescribible, pero ahora hay que pedirlo a propósito
 * en vez de acertarle de memoria cada vez.
 */
export function EmailField({
  name = "email",
  purpose = "contact",
  ...rest
}: EmailFieldProps) {
  return (
    <TextField
      label="Correo"
      placeholder="hola@itsthebest1111.com"
      type="email"
      inputMode="email"
      autoComplete={AUTOCOMPLETE_BY_PURPOSE[purpose]}
      {...rest}
      name={name}
    />
  );
}
